import React, { useState, useEffect } from 'react';
import Login                from './components/Login';
import CalcLogin            from './components/CalcLogin';
import ResetPassword        from './components/ResetPassword';
import LandingPage          from './components/LandingPage';
import Screener             from './components/Screener';
import Trades               from './components/Trades';
import StrategyAnalyzer     from './components/StrategyAnalyzer';
import DataTab              from './components/DataTab';
import MarketVolatility     from './components/MarketVolatility';
import Marginality          from './components/Marginality';
import Workers              from './components/Workers';
import ClusterViewer        from './components/ClusterViewer';
import Forecasts            from './components/Forecasts';
import SingleTickerBacktest from './components/SingleTickerBacktest';
import CalcAccountPage      from './components/account/CalcAccountPage';
import Admin                from './components/Admin';
import ConnectionLog        from './components/ConnectionLog';
import RunningBots          from './components/RunningBots';
import Demo                 from './components/Demo';
import TradingTickerPage    from './components/TradingTickerPage';
import TradingPublicSite    from './components/trading/public-site/TradingPublicSite';
import TradingUserHome      from './components/trading/TradingUserHome';
import PendingVirtualStart  from './components/trading/PendingVirtualStart';
import TradingAccountPage   from './components/trading/TradingAccountPage';
import AppShell             from './components/layout/AppShell';
import ErrorBoundary        from './components/ErrorBoundary';
import { getMe }            from './api';

/* ── Build-time variant: 'calc' (backtesting.trading.tryit.pp.ua) | 'trading' (trading.* + app.trading.*) ── */
const VARIANT = process.env.REACT_APP_VARIANT || 'calc';

/* ── Runtime surface (тільки коли VARIANT='trading') ─────────────────────
 * Один tradingapp-frontend обслуговує ОБИДВА хости:
 *   app.trading.tryit.pp.ua  → 'app'    (login → cabinet → live bot)
 *   trading.tryit.pp.ua      → 'public' (TradingPublicSite; "Вхід"/"Зареєструватися" → app.*)
 * REACT_APP_FORCE_SURFACE дозволяє local dev override без /etc/hosts.
 */
const FORCE_SURFACE = process.env.REACT_APP_FORCE_SURFACE;
const IS_APP_HOST   = typeof window !== 'undefined' && /^app\./.test(window.location.host);
const SURFACE       = VARIANT === 'trading'
  ? (FORCE_SURFACE || (IS_APP_HOST ? 'app' : 'public'))
  : 'calc';

/* ── Routes ─────────────────────────────────────────────────────────────── */
// '/' тут — НЕ сторінка: колишній дашборд живого бота (крива капіталу, Start/Stop)
// до бектестингу не стосується, тож головна одразу веде в аналізатор
// (див. canonicalPath). Невідомі адреси теж падають в аналізатор.
const ROUTES_CALC = [
  { path: '/analyzer/:id/:ticker',  component: 'analyzer'          },
  { path: '/analyzer/:id',          component: 'analyzer'          },
  { path: '/analyzer',              component: 'analyzer'          },
  // /analyzer-advanced was a second, menu-less copy of the analyzer from before the
  // advanced engine was folded into StrategyAnalyzer. The page is gone; the routes stay
  // as redirects so old bookmarks land on the real analyzer instead of a blank screen.
  { path: '/analyzer-advanced/:id', component: 'analyzer'          },
  { path: '/analyzer-advanced',     component: 'analyzer'          },
  { path: '/single-backtest',      component: 'single'     },
  { path: '/market-volatility',    component: 'market'     },
  { path: '/marginality',          component: 'marginality' },
  // Стан машин, що рахують перебір (лише адмін).
  { path: '/cluster',              component: 'workers'    },
  { path: '/workers',              component: 'workers'    },  // стара адреса → /cluster
  { path: '/data',                 component: 'data'       },
  // Прогнози (підпроєкт forecasts, лише адмін).
  { path: '/forecasts',            component: 'forecasts'  },
  { path: '/screener',             component: 'screener'   },
  { path: '/trades/ticker/:ticker', component: 'trades-ticker' },
  { path: '/trades',               component: 'trades'     },
  { path: '/running',               component: 'running'    },
  // Профіль (з 03.11 адреса /settings, як у кабінеті бота; /my-account — псевдонім).
  { path: '/settings',             component: 'my-account' },
  { path: '/my-account',           component: 'my-account' },
  { path: '/admin',                component: 'admin'      },
  { path: '/connection-log',       component: 'connection-log' },
  { path: '/demo',                 component: 'demo'       },
];

// NB: no '/demo' here — on the trading variant /demo is the PUBLIC demo-cabinet
// page (TradingPublicSite route), not the calc UI-kit Demo component.
const ROUTES_TRADING = [
  { path: '/',               component: 'dashboard'      },
  // Довідка «під що брокер дає кредит» — відкривається з налаштувань бота,
  // з-під галочки «Купувати лише marginable акції»: таблиця показує, які саме
  // папери ця галочка відсіює і чому (кредит / лише за свої / купити не можна).
  { path: '/marginality',    component: 'marginality'    },
  // Налаштування акаунта (з 13.10 адреса /settings; стара /my-account лишається
  // псевдонімом — canonicalPath переписує її, тож старі посилання й листи живі).
  { path: '/settings',       component: 'my-account'     },
  { path: '/my-account',     component: 'my-account'     },
  { path: '/admin',          component: 'admin'          },
  { path: '/connection-log', component: 'connection-log' },
  { path: '/running',        component: 'running'        },
];

const ROUTES = VARIANT === 'trading' ? ROUTES_TRADING : ROUTES_CALC;
const FALLBACK_COMPONENT = VARIANT === 'trading' ? 'dashboard' : 'analyzer';

// Адреса, яку має показувати рядок браузера. На бектестингу головна = аналізатор,
// тож '/' переписується на '/analyzer' — інакше пункт «Стратегії» не підсвічувався б,
// а оновлення сторінки лишало б користувача на «голій» адресі.
// '/settings' колись була налаштуваннями mock-бота; з 03.11 це профіль
// користувача (як на app.*), а стара адреса профілю /my-account переписується сюди.
const CALC_RETIRED_PATHS = ['/', ''];
function canonicalPath(pathname) {
  if (VARIANT !== 'trading' && CALC_RETIRED_PATHS.includes(pathname)) return '/analyzer';
  if (pathname === '/my-account') return '/settings';
  // Сторінка машин парку перейменована на «Кластер» (менеджер 16.10).
  if (VARIANT !== 'trading' && pathname === '/workers') return '/cluster';
  // Залогіненому /login і /register — не сторінки, а вхід у кабінет: кнопка
  // «Вхід» основного сайту веде на app.<host>/login, і без цього портфель
  // показувався з «/login» в адресі (і при поверненні «назад» теж).
  if ((pathname === '/login' || pathname === '/register') && localStorage.getItem('token')) return '/';
  return pathname;
}

/* ── Nav items ───────────────────────────────────────────────────────────── */
// Меню бектестингу — тільки те, чим тут справді користуються (26.08).
// Прибрано чотири бот-пункти: Головна (/), Тестування стратегій (/running),
// Моніторинг (/screener), Торгівля (/trades). Усі четверо показують стан
// ЖИВОГО бота, якого в цій збірці немає: /running взагалі віддавав «Помилка
// завантаження».
//
// ⚠️ Прибрано САМЕ З МЕНЮ, маршрути лишились у ROUTES_CALC: старі посилання й
// закладки далі відкриваються, а '/' лишається запасним маршрутом, куди
// matchRoute віддає все невідоме. Повернути пункт = один рядок тут.
const NAV_ITEMS_CALC = [
  { id: 'analyzer',   label: 'Стратегії',      path: '/analyzer'        },
  { id: 'data',       label: 'Історичні дані', path: '/data'            },
  { id: 'market',     label: 'Волатильність',  path: '/market-volatility'},
  { id: 'marginality', label: 'Маржинальність', path: '/marginality'      },
  { id: 'forecasts',  label: 'Прогнози',       path: '/forecasts', adminOnly: true },
];

// Меню кабінету (менеджер 13.10): «Мій акаунт» = портфель на головній,
// «Налаштування» = колишня сторінка «Мій акаунт». Адміністрування додає
// AppShell (лише адмін, і не на телефоні).
const NAV_ITEMS_TRADING = [
  { id: 'dashboard',  label: 'Мій акаунт',   path: '/'         },
  { id: 'my-account', label: 'Налаштування', path: '/settings' },
];

const NAV_ITEMS = VARIANT === 'trading' ? NAV_ITEMS_TRADING : NAV_ITEMS_CALC;

/* ── Route matcher ───────────────────────────────────────────────────────── */
function matchRoute(pathname) {
  for (const route of ROUTES) {
    const pattern = route.path
      .replace(':id',     '([0-9]+)')
      .replace(':ticker', '([A-Za-z0-9.^-]+)');
    const regex = new RegExp(`^${pattern}$`, 'i');
    const match = pathname.match(regex);
    if (match) {
      const params = {};
      const paramNames = (route.path.match(/:[a-z]+/g) || []).map(p => p.slice(1));
      paramNames.forEach((name, i) => { params[name] = match[i + 1]; });
      return { component: route.component, params };
    }
  }
  return { component: FALLBACK_COMPONENT, params: {} };
}


/* ── Main App ────────────────────────────────────────────────────────────── */
export default function App() {
  // Apply variant class to <body> so CSS can target trading vs calc theme
  useEffect(() => { document.body.classList.add(`variant-${VARIANT}`); }, []);

  // Заголовок вкладки для бектест-збірки. public/index.html спільний з
  // trading-збіркою (лендинг), тому підпис ставимо в рантаймі, а не там.
  useEffect(() => {
    if (VARIANT === 'calc') document.title = 'Algo Trading Platform';
  }, []);

  const [isLoggedIn,   setIsLoggedIn]   = useState(!!localStorage.getItem('token'));
  const [username,     setUsername]     = useState(localStorage.getItem('username') || '');
  const [userRole,     setUserRole]     = useState(localStorage.getItem('role') || 'user');
  const [showLogin,    setShowLogin]    = useState(window.location.pathname === '/login');
  const [routeInfo,    setRouteInfo]    = useState(() => {
    const path = canonicalPath(window.location.pathname);
    if (path !== window.location.pathname) window.history.replaceState({}, '', path + window.location.search);
    return matchRoute(path);
  });
  const [singleTicker, setSingleTicker] = useState('');

  function navigateTo(path) {
    if (typeof path === 'number') { window.history.go(path); return; }
    path = canonicalPath(path);
    // Порівнюємо й query: з графіка тікера (/?ticker=X) «Мій акаунт» веде на ту
    // саму '/', і без нового запису в історії графік лишався б відкритим.
    if (window.location.pathname !== path || window.location.search) {
      window.history.pushState({}, '', path);
    }
    setRouteInfo(matchRoute(path));
    window.dispatchEvent(new Event('app-navigate'));
  }

  useEffect(() => {
    const handler = () => {
      const path = canonicalPath(window.location.pathname);
      if (path !== window.location.pathname) window.history.replaceState({}, '', path);
      setRouteInfo(matchRoute(path));
    };
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, []);

  // Роль у localStorage записується лише при вході. Якщо людину зробили
  // адміном пізніше, пристрій, де вона вже залогінена (телефон), так і
  // показував меню звичайного користувача (менеджер 16.10). Сервер і так
  // перевіряє роль з БД, тож тут лише підтягуємо її для меню.
  useEffect(() => {
    if (!isLoggedIn) return;
    getMe().then(({ data }) => {
      // Ковзна сесія: сервер щоразу віддає свіжий токен на повний строк.
      if (data?.token) localStorage.setItem('token', data.token);
      const role = data?.role || 'user';
      localStorage.setItem('role', role);
      setUserRole(role);
    }).catch(() => {});
  }, [isLoggedIn]);

  useEffect(() => {
    const handleLogout = () => {
      setIsLoggedIn(false);
      setUsername('');
      setShowLogin(false);
      navigateTo('/');
    };
    window.addEventListener('auth:logout', handleLogout);
    return () => window.removeEventListener('auth:logout', handleLogout);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      setSingleTicker(e.detail?.ticker || '');
      navigateTo('/single-backtest');
    };
    window.addEventListener('goto:single-backtest', handler);
    return () => window.removeEventListener('goto:single-backtest', handler);
  }, []);

  useEffect(() => {
    const handler = () => navigateTo('/running');
    window.addEventListener('goto:running', handler);
    return () => window.removeEventListener('goto:running', handler);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      const isSave = (e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'S');
      if (!isSave) return;
      e.preventDefault();
      const html = '<!DOCTYPE html>\n' + document.documentElement.outerHTML;
      const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      const safePath = window.location.pathname.replace(/[^a-zA-Z0-9_-]+/g, '_').replace(/^_+|_+$/g, '') || 'home';
      a.href = url;
      a.download = `trading-${safePath}-${new Date().toISOString().slice(0,10)}.html`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  function handleLogin(token, uname, role = 'user') {
    localStorage.setItem('token',    token);
    localStorage.setItem('username', uname);
    localStorage.setItem('role',     role);
    setIsLoggedIn(true);
    setUsername(uname);
    setUserRole(role);
    // /login|/register|/reset-password не є сторінками кабінету — після входу
    // показуємо «Мій портфель» на своєму URL (/).
    if (['/login', '/register', '/reset-password'].includes(window.location.pathname)) {
      const home = canonicalPath('/');
      window.history.replaceState({}, '', home);
      setRouteInfo(matchRoute(home));
    }
  }

  function handleLogout() {
    localStorage.removeItem('token');
    localStorage.removeItem('username');
    localStorage.removeItem('avatar');
    localStorage.removeItem('role');
    setIsLoggedIn(false);
    setUsername('');
    setUserRole('user');
    setShowLogin(false);
    window.history.pushState({}, '', canonicalPath('/'));
    setRouteInfo(matchRoute(canonicalPath('/')));
  }

  if (routeInfo.component === 'demo') return <Demo />;

  // Скидання паролю (відкривається з листа) — працює і залогіненим, і ні.
  if (typeof window !== 'undefined' && window.location.pathname === '/reset-password') {
    return <ResetPassword onLogin={handleLogin} />;
  }

  // /cluster для не-адміна (і без входу взагалі) — перегляд за паролем, який
  // задає адмін на самій сторінці. Адмін бачить звичайну сторінку в кабінеті.
  if (VARIANT !== 'trading' && routeInfo.component === 'workers'
      && (!isLoggedIn || userRole !== 'admin')) {
    return <ClusterViewer />;
  }

  if (!isLoggedIn) {
    // Public surface (trading.*): маркетинговий сайт (власний react-router-dom
    // всередині); "Вхід"/"Зареєструватися" → external app.<host>.
    if (SURFACE === 'public') {
      return <TradingPublicSite />;
    }
    // App surface (app.trading.*): пропускаємо лендінг, одразу Login.
    if (SURFACE === 'app') {
      return <Login onLogin={handleLogin} />;
    }
    // CALC: одразу логін без лендінгу.
    return <CalcLogin onLogin={handleLogin} />;
  }

  // Залогінений user на public surface — показуємо публічний сайт як є:
  // лого/футер з кабінету ведуть сюди (пп.3/4), тож редірект назад в кабінет
  // робив би ці посилання марними. В кабінет веде кнопка "Вхід" (app.<host>).
  if (SURFACE === 'public') {
    return <TradingPublicSite />;
  }

  const { component, params } = routeInfo;

  /* ── active nav detection ── */
  const activeNav = ['analyzer', 'single'].includes(component) ? 'analyzer'
    : component === 'trades-ticker' ? 'trades'
    : component;

  return (
    <AppShell
      component={component}
      navigateTo={navigateTo}
      username={username}
      userRole={userRole}
      onLogout={handleLogout}
      activeNav={activeNav}
      navItems={NAV_ITEMS.filter((it) => !it.adminOnly || userRole === 'admin')}
      variant={VARIANT}
    >
      {/* 🚨 Помилка рендера всередині сторінки більше не гасить увесь кабінет:
          без цієї обгортки React демонтував дерево, і лишався чорний екран з
          однією стрічкою в консолі (спіймано двічі за день). */}
      <ErrorBoundary key={component}>
      {component === 'dashboard'  && VARIANT === 'trading' && <TradingUserHome userRole={userRole} navigateTo={navigateTo} />}
      {component === 'screener'   && <Screener />}
      {component === 'trades'        && <Trades navigateTo={navigateTo} />}
      {component === 'trades-ticker' && (
        <TradingTickerPage
          ticker={params.ticker}
          onBack={() => navigateTo('/trades')}
        />
      )}
      {component === 'running'    && <RunningBots />}
      {component === 'data'       && <DataTab />}
      {component === 'market'     && <MarketVolatility />}
      {component === 'marginality' && <Marginality />}
      {component === 'workers'    && userRole === 'admin' && <Workers />}
      {component === 'forecasts'  && userRole === 'admin' && <Forecasts />}
      {component === 'single'     && <SingleTickerBacktest initialTicker={singleTicker} />}
      {component === 'my-account' && (VARIANT === 'trading' ? <TradingAccountPage navigateTo={navigateTo} /> : <CalcAccountPage />)}
      {component === 'admin'      && userRole === 'admin' && <Admin />}
      {component === 'connection-log' && userRole === 'admin' && <ConnectionLog />}
      {component === 'demo'       && <Demo />}
      {component === 'analyzer'   && (
        <StrategyAnalyzer
          initialResultId={params.id}
          initialTicker={params.ticker}
          navigateTo={navigateTo}
        />
      )}
      </ErrorBoundary>
      {/* Замовлення віртуальних торгів з лендінгу (кука vt_pending_v1) — рішення тут,
          бо лише кабінет знає, чи є в користувача реальні/віртуальні торги. */}
      {VARIANT === 'trading' && <PendingVirtualStart navigateTo={navigateTo} />}
    </AppShell>
  );
}
